import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getReceipt, getStores, updateReceiptStore } from '../services/api';
import { format } from 'date-fns';

function StoreEditor({ receipt, onSaved, onCancel }) {
  const [stores, setStores] = useState([]);
  const [selection, setSelection] = useState('new');
  const [newName, setNewName] = useState(receipt.store_name || '');
  const [newChain, setNewChain] = useState(receipt.store_chain || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getStores().then(setStores).catch(() => {});
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const patch = selection === 'new'
        ? { name: newName.trim(), chain: newChain.trim() }
        : { store_id: Number(selection) };
      const updated = await updateReceiptStore(receipt.id, patch);
      onSaved(updated);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update store');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSave} className="card" style={{ marginTop: '0.5rem', marginBottom: '1rem' }}>
      <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>Store</label>
      <select
        value={selection}
        onChange={(e) => setSelection(e.target.value)}
        style={{ marginBottom: '0.75rem', width: '100%', maxWidth: 320 }}
      >
        <option value="new">— Rename / add new store —</option>
        {stores.map((s) => (
          <option key={s.id} value={s.id}>{s.name} ({s.chain})</option>
        ))}
      </select>

      {selection === 'new' && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '0.75rem' }}>
          <input
            type="text"
            placeholder="Store name, e.g. Coop Extra Løkka"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            required
            style={{ flex: '1 1 220px' }}
          />
          <input
            type="text"
            placeholder="Chain, e.g. Coop"
            value={newChain}
            onChange={(e) => setNewChain(e.target.value)}
            required
            style={{ flex: '1 1 140px' }}
          />
        </div>
      )}

      {error && <p className="error" style={{ fontSize: 13 }}>{error}</p>}

      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" className="btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
      </div>
      <p className="muted" style={{ fontSize: 12, marginTop: '0.75rem', marginBottom: 0 }}>
        This is remembered — future receipts with a similarly-scanned header will use this store automatically.
      </p>
    </form>
  );
}

export default function ReceiptDetailPage() {
  const { id } = useParams();
  const [receipt, setReceipt] = useState(null);
  const [error, setError] = useState('');
  const [editingStore, setEditingStore] = useState(false);

  useEffect(() => {
    getReceipt(id)
      .then(setReceipt)
      .catch(() => setError('Receipt not found'));
  }, [id]);

  if (error) return <p className="error">{error}</p>;
  if (!receipt) return <p className="muted">Loading…</p>;

  return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <Link to="/receipts" className="muted" style={{ fontSize: 13 }}>← Back to receipts</Link>
        <h2 style={{ marginTop: '0.5rem' }}>
          {receipt.store_chain} — {receipt.purchase_date ? format(new Date(receipt.purchase_date), 'dd MMM yyyy') : '—'}
        </h2>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center', color: 'var(--color-text-muted)', fontSize: 13 }}>
          <span>{receipt.store_name}</span>
          {!editingStore && (
            <button
              type="button"
              className="btn-secondary"
              style={{ padding: '2px 10px', fontSize: 12 }}
              onClick={() => setEditingStore(true)}
            >
              Fix store name
            </button>
          )}
          <span>Total: <strong style={{ color: 'var(--color-text)' }}>
            {Number(receipt.total_amount).toFixed(2)} {receipt.currency}
          </strong></span>
          {receipt.ai_parsed && <span style={{ color: 'var(--color-success)' }}>AI parsed</span>}
        </div>
        {editingStore && (
          <StoreEditor
            receipt={receipt}
            onCancel={() => setEditingStore(false)}
            onSaved={(updated) => { setReceipt(updated); setEditingStore(false); }}
          />
        )}
      </div>

      <div className="card" style={{ padding: 0, marginBottom: '1.5rem' }}>
        <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Item</th>
              <th>Category</th>
              <th style={{ textAlign: 'right' }}>Qty</th>
              <th>Unit</th>
              <th style={{ textAlign: 'right' }}>Price</th>
              <th style={{ textAlign: 'right' }}>Per unit</th>
              <th style={{ textAlign: 'right' }}>VAT</th>
            </tr>
          </thead>
          <tbody>
            {receipt.line_items?.map((li) => (
              <tr key={li.id}>
                <td>{li.raw_text}</td>
                <td>{li.category_name || <span className="muted">—</span>}</td>
                <td style={{ textAlign: 'right' }}>{Number(li.quantity)}</td>
                <td className="muted">{li.unit || '—'}</td>
                <td style={{ textAlign: 'right' }}>{Number(li.price).toFixed(2)}</td>
                <td style={{ textAlign: 'right', color: 'var(--color-text-muted)' }}>
                  {li.price_per_canonical_unit
                    ? `${Number(li.price_per_canonical_unit).toFixed(2)} kr/${li.canonical_unit || li.unit}`
                    : '—'}
                </td>
                <td className="muted" style={{ textAlign: 'right' }}>
                  {li.tax_rate != null ? `${Number(li.tax_rate)}%` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {receipt.raw_text && (
        <div className="card">
          <h4 style={{ marginBottom: '0.75rem' }}>Raw receipt text</h4>
          <pre style={{
            whiteSpace: 'pre-wrap',
            fontFamily: 'monospace',
            fontSize: 12,
            color: 'var(--color-text-muted)',
            margin: 0,
          }}>{receipt.raw_text}</pre>
        </div>
      )}
    </div>
  );
}
